import { pick } from 'lodash-es';

/**
 * Waits for the provided amount of time, then resolves.
 * @param {Number} t - Time to wait in milliseconds.
 * @returns {Promise<void>} Resolves after t milliseconds.
 */
export const sleep = (t) => new Promise((resolve) => {
  setTimeout(() => {
    resolve();
  }, t);
});

const MAX_RETRIES = 5;
const RETRY_DELAY = 250;

/**
 * Takes a promise-returning function, and returns a new function.
 * This new function will call itself repeatedly until `fn` either succeeds,
 * or the max number of retries is exceeded.
 * @param {Function<Promise>} fn - Function that returns a promise.
 * @param {Number} [maxRetries] - Max number of retry attempts before throwing error.
 * @returns {Function<Promise<*>>} Function that will return the response from `fn()`.
 */
export const withRetries = (fn, maxRetries = MAX_RETRIES) => {
  const wrapper = async (args = [], attempts = 0) => {
    try {
      const resp = await fn(...args);
      return resp;
    } catch (err) {
      if (attempts >= maxRetries) {
        // Bubble up the error for the caller to handle
        throw err;
      }

      // Keep retrying
      await sleep(RETRY_DELAY);
      return wrapper(args, attempts + 1);
    }
  };

  return wrapper;
};

/**
 * Converts a string from snake_case, camelCase, or PascalCase to a more readable format,
 * with each word capitalized and separated by spaces.
 * @param {String} str - The string to be formatted.
 * @returns {String} The formatted string, with words capitalized and separated by spaces.
 */
export const createReadableLabel = (str) => {
  if (str && str.length) {
    const snakeToCapitalized = str.split('_').map((word) => word[0].toUpperCase() + word.substring(1)).join(' ');  // Pre-formatting capitalization and snake case handling
    const pascalCaseMatches = snakeToCapitalized.match(/($[a-z])|[A-Z][^A-Z]+/g);
    const isPascalCase = pascalCaseMatches.length > 1 && !snakeToCapitalized.includes(' ');
    return (isPascalCase) ? pascalCaseMatches.join(' ') : snakeToCapitalized;
  }

  return '';
};

export function normalizePostForEvents(post) {
  const postKeysToKeep = [
    'network',
    'post_type',
    'post_id',
    '_id',
    'permalink',
    'user_name',
    'curated_time',
    'mediaCached',
    'tgb_meta',
    'owner',
    'tagboard_id',
    'from_tagboard',
    'timeline_id',
  ];
  return pick(post, postKeysToKeep);
}
